import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { Role } from '@flowus/common';
import { isPageLike } from '@flowus/common/block/permissions';
import { cx } from '@flowus/common/cx';
import { Tooltip } from '@flowus/common/tooltip';
import { PermissionRole } from '@next-space/fe-api-idl';
import { useMemo, type FC } from 'react';
import { Icon } from 'src/common/components/icon';
import { useBuyGuestCount } from 'src/components/buy-guest-count-modal';
import { useOpenUpgradeSpacePlan } from 'src/components/upgrade-space-plan';
import { useBlock } from 'src/hooks/block/use-block';
import { useCurrentSpace } from 'src/hooks/space';
import { getSpaceRolePermission } from 'src/hooks/space/use-get-space-role-permission';
import { useIsProSpace } from 'src/hooks/space/use-is-pro-space';
import { useSpaceSecurityState } from 'src/hooks/space/use-space-security-state';
import { useCurrentUser } from 'src/hooks/user/use-current-user';
import { useGuestsList } from 'src/services/app/hook';
import { useLimitConfig } from 'src/services/app/hook/subscription-data';
import { ViewPath } from 'src/utils';
import { getSpacePlanTypeName } from 'src/utils/block-utils';
import { writeTextInClipboard } from 'src/utils/clipboard';
import { getLocationOrigin } from 'src/utils/location-utils';
import { OpenSettingFrom, SettingMenuType } from 'src/views/main/setting-modal/type';
import { useOpenSettingModal } from 'src/views/main/setting-modal/use-open-setting-modal';
import { SearchBar } from '../components/search-bar';
import { useSharePanel } from '../components/use-share-panel';
import { UserList } from '../components/user-list';
import { useOpenSynergyInvitation } from '../search-user/search-user-select';
import type { ShareProps } from '../types';
import { Panel } from '../types';
import { UpgradeSpacePlanType } from 'src/components/upgrade-space-plan/types';
import { useRestoreRole } from 'src/hooks/block/use-restore-role';

const NUMBERS = 5;

export const ShareMainInvite: FC<Pick<ShareProps, 'uuid' | 'onChangeTab'>> = ({
  uuid,
  onChangeTab,
}) => {
  const { t } = useAppTranslation();
  const guests = useGuestsList();
  const isProSpace = useIsProSpace();
  const { isMaxGuests, guestMaxLimitDesc } = useLimitConfig();
  const block = useBlock(uuid);
  const sharePanel = useSharePanel(uuid);
  const openSynergyInvitation = useOpenSynergyInvitation();
  const abandonedGuest = useSpaceSecurityState('abandonedGuest');
  const currentSpace = useCurrentSpace();
  const spacePlanName = getSpacePlanTypeName(currentSpace.planType);
  const openSettingModal = useOpenSettingModal();
  const currentUser = useCurrentUser();
  const spaceEditor = getSpaceRolePermission(currentUser.uuid).editor;
  const openUpgradeSpacePlan = useOpenUpgradeSpacePlan();
  const restoreRole = useRestoreRole();
  const buyGuestCount = useBuyGuestCount();

  const isShowRestore = useMemo(() => {
    const spaceEditor = getSpaceRolePermission(currentUser.uuid).editor;
    return spaceEditor;
  }, [currentUser.uuid]);

  const copyPageLink = async () => {
    if (isPageLike(block?.type)) {
      void writeTextInClipboard(`${getLocationOrigin()}/${uuid}`);
    } else {
      void writeTextInClipboard(`${getLocationOrigin()}${ViewPath.preview}${uuid}`);
    }
  };

  if (!sharePanel) return null;

  const { role, permissions, onChange } = sharePanel;

  const popup = (
    <div className="p-1 space-y-1">
      <div>
        {t('components.share.share_main_invite.space_plan_invite_desc', {
          spacePlanName,
          guestMaxLimitDesc,
        })}
      </div>
      <div
        className="text-active_color animate-click text-right"
        onClick={() => {
          openSettingModal({
            defaultMenuType: SettingMenuType.members,
            from: OpenSettingFrom.guest_max,
          });
        }}
      >
        {t('components.share.share_main_invite.view_my_space_members')}
      </div>
    </div>
  );

  return (
    <>
      <div className="mx-4 text-t2">
        {/* 免费版空间：显示升级入口 */}
        {!isProSpace && (
          <div className="flex items-center justify-between w-full pt-2.5 pb-1">
            <div className="flex items-center space-x-1">
              <span>
                {t('components.share.share_main_invite.space_invited_external_collaborators')}
                <span className={cx('px-1', isMaxGuests && 'text-red')}>
                  {guests.length}/{guestMaxLimitDesc}
                </span>
                {t('common.people')}
              </span>
              <Tooltip popup={popup}>
                <Icon className="text-grey3" size="small" name="IcQuestion" />
              </Tooltip>
            </div>
            <span
              hidden={!spaceEditor}
              className="text-t4 text-active_color animate-click"
              onClick={() => {
                openUpgradeSpacePlan({ type: UpgradeSpacePlanType.maxGuestCount });
              }}
            >
              {t('components.share.share_main_invite.upgrade_for_more_seats')}
            </span>
          </div>
        )}

        {/* 专业版/团队版空间：显示购买入口 */}
        {!__BUILD_IN__ && isProSpace && (
          <div className="flex items-center justify-between w-full pt-2.5 pb-1">
            <div className="flex items-center space-x-1">
              <span>
                {t('components.share.share_main_invite.space_invited_external_collaborators')}
                <span className={cx('px-1', isMaxGuests && 'text-red')}>
                  {guests.length}/{guestMaxLimitDesc}
                </span>
                {t('common.people')}
              </span>
              <Tooltip popup={popup}>
                <Icon className="text-grey3" size="small" name="IcQuestion" />
              </Tooltip>
            </div>
            <span
              hidden={!spaceEditor}
              className="text-t4 text-active_color animate-click"
              onClick={() => {
                buyGuestCount({ spaceId: currentSpace.uuid });
              }}
            >
              {t('common.buy_more_guests')}
            </span>
          </div>
        )}

        <SearchBar
          uuid={uuid}
          onClick={() => onChangeTab(Panel.SEARCH_USER)}
          className="invite-bar my-2"
        />

        <UserList
          showRight
          filterUserNoInSpace
          onChange={role === PermissionRole.EDITOR ? onChange : undefined}
          permissions={permissions.slice(0, NUMBERS)}
        />

        {permissions.length > NUMBERS && (
          <div
            onClick={() => onChangeTab(Panel.ALL_USERS)}
            className="flex items-center animate-click animate-hover p-1"
          >
            <Icon size="middle" className="text-grey4" name="IcShareAnyone" />
            <div className="text-t3 text-grey4 pl-4">
              {t('components.share.share_main_invite.view_all_collaborators', {
                count: permissions.length,
              })}
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between pt-1 pb-2.5 px-4 text-t4 text-grey3">
        <div />
        <div className="flex items-center space-x-2 text-active_color text-t4">
          <span className="animate-click" hidden={!isShowRestore} onClick={() => restoreRole(uuid)}>
            {t('components.share.share_main_invite.reset_permissions')}
          </span>
          {Role.contains(role, PermissionRole.EDITOR) && !abandonedGuest && (
            <div
              className="flex space-x-1 items-center animate-click"
              onClick={openSynergyInvitation}
            >
              <Icon size="small" name="IcMenuPerson" />
              <span>{t('components.share.share_main_invite.invite_by_link')}</span>
            </div>
          )}
          <div className="flex space-x-1 items-center animate-click" onClick={copyPageLink}>
            <Icon size="small" name="IcLink" />
            <span>{t('common.copy_page_link')}</span>
          </div>
        </div>
      </div>
    </>
  );
};
